import { Button, TableItem } from 'components/atoms';
import { Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { useTableColumnsVisibility } from 'hooks/useTableColumnsVisibility';
import { resources } from 'locales/i18n';
import { useMemo } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { PiMagicWand, PiWarningOctagon } from 'react-icons/pi';
import { useGetLanguages } from 'services/enums';
import { Column } from 'types/components/table/table.types';

enum SORT_COLUMNS {
  id,
  code,
  en,
  pl,
}

const Languages = () => {
  const { t } = useTranslation();

  const columnsVisibility = useTableColumnsVisibility(
    'LanguagesDevTools',
    SORT_COLUMNS
  );

  const languages = useGetLanguages();

  const languageNodes = useMemo(
    () =>
      languages.data?.map(e => ({
        id: `${e.id}`,
        code: `${e.code}`,
        name: `${e.name}`,
      })) ?? [],
    [languages.data]
  );

  const appLanguages = useMemo<string[]>(
    () => Object.keys(resources),
    [resources]
  );

  const onClickToCopy = (title: string, value: string) => {
    toast.success(`${title} ${value} has been copied to clipboard!`);
    navigator.clipboard.writeText(value);
  };

  const getTranslation = (lng: string, id: string) =>
    t(`enum.LANGUAGE.${id}`, { lng: lng });

  const getMissingLanguages = () => {
    const missingLanguagesByLanguage = appLanguages.map(l => ({
      [l]: languageNodes
        .filter(e => getTranslation(l, `${e.id}`).includes('enum.LANGUAGE.'))
        .map(e => e.id),
    }));

    console.log('Missing languages translations:', missingLanguagesByLanguage);
    toast.success(`Check the console for result`);
  };

  return (
    <TopbarPage
      topbar={
        <TopBar
          title={`Languages list (${languages.data?.length ?? 0})`}
          hideBackButton
          topbarContent={
            <>
              <Button
                disabled={languages.isLoading}
                isClear
                onlyIcon
                onClick={getMissingLanguages}>
                <Button.Icon icon={<PiMagicWand />} />
              </Button>
              <Table.CustomColumnsButton
                columns={SORT_COLUMNS}
                isClear
                visibility={columnsVisibility}
                i18nextPrefix=' '
                customI18next=' '
              />
            </>
          }
        />
      }>
      <Table
        id={columnsVisibility.tableId}
        loading={languages.isLoading}
        size='compact'
        // displayMode
        data={{
          nodes: languageNodes,
        }}
        columns={[
          {
            label: 'Id',
            renderCell: ({ id }) => (
              <TableItem
                className='cursor-pointer'
                onClick={() => onClickToCopy('id', id)}>
                <TableItem.Text text={`${id}`} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(SORT_COLUMNS.id),
          },
          {
            label: 'Code',
            renderCell: ({ code }) => (
              <TableItem
                className='cursor-pointer'
                onClick={() => onClickToCopy('code', code)}>
                <TableItem.Text text={`${code}`} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(SORT_COLUMNS.code),
          },
          ...appLanguages.map<Column<{ id: string }>>((l, key) => ({
            label: l.toUpperCase(),
            renderCell: ({ id }) => (
              <TableItem>
                {getTranslation(l, id).includes('enum.LANGUAGE.') ? (
                  <TableItem.Label color='red' size='compact'>
                    <PiWarningOctagon /> NOT FOUND
                  </TableItem.Label>
                ) : (
                  <TableItem.Text text={getTranslation(l, id)} />
                )}
              </TableItem>
            ),
            resize: key !== appLanguages.length - 1,
            hide: columnsVisibility.hide(
              SORT_COLUMNS[l as keyof typeof SORT_COLUMNS]
            ),
          })),
        ]}
      />
    </TopbarPage>
  );
};

export default Languages;
